'use client';
import RoundedContainer from '@/src/features/ui/rounded-container';
import { formatInterestRate } from '@/src/utils/helpers/replace-dot-with-comma';
import React, { useState } from 'react';
import Tooltip from '../../../query-item/tooltip';
import fmt from '@/src/utils/helpers/number-format';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import LinkLikeButton from '@/src/features/shared/link-like-button';
import { ResponseStoreFrontVehicleLoanDetail } from '@/src/interfaces/data-contracts';
import { Button } from '@/src/features/ui/button/button';
import Modal from '../../../modal';
import ImageWithFallback from '@/src/features/ui/image/image-with-fallback';
import { getCdnPath } from '@/src/utils/get-cdn-path';
import Heading from '@/src/features/ui/heading';

type ContentProps = {
  summaryContentObj: (
    | {
        name: string;
        value: string;
        hasTooltip: boolean;
      }
    | {
        name: string;
        value: string | undefined;
        hasTooltip?: boolean;
      }
  )[];
  item: ResponseStoreFrontVehicleLoanDetail;
  forwardProduct: any;
  openPopup: () => void;
  setIsPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isPopupOpen: boolean;
};

const Content = ({ summaryContentObj, item, forwardProduct, openPopup, setIsPopupOpen, isPopupOpen }: ContentProps) => {
  return (
    <div className="flex flex-col px-4 md:gap-7 md:px-0">
      <section className="flex flex-col divide-y border-t first:border-0 md:border-0">
        {summaryContentObj.map((item) => {
          return (
            <React.Fragment key={item.name}>
              <div className="flex flex-row justify-between pb-3 pt-3 md:not-first-child:pt-3">
                <div className="flex items-center gap-1">
                  <p className="text-neutral500 md:text-base">{item.name}</p>
                  {item.hasTooltip && (
                    <Tooltip isNegative optionalClasses="md:text-xs md:w-[233px] whitespace-nowrap">
                      Faiz oranı şirketinize göre değişebilir.
                    </Tooltip>
                  )}
                </div>
                <p className="font-semibold text-neutral800 md:text-base">{item.value}</p>
              </div>
            </React.Fragment>
          );
        })}
      </section>
      {item.isActiveRecourse && item.recourseLink && (
        <LinkLikeButton
          target={item.target}
          href={item.recourseLink}
          text={item?.redirectButtonText ?? 'Hemen Başvur'}
          className="h-[48px] w-full rounded-lg text-sm after:ml-2 after:text-xl after:content-['↗'] md:text-base"
        />
      )}

      {item && !item.isActiveRecourse && forwardProduct && (
        <>
          <Button
            className="mt-8 flex h-[48px] items-center justify-center rounded-lg border border-purple500 text-sm font-bold after:ml-2 after:text-xl after:content-['↗'] md:text-base"
            text="Hemen Başvur"
            onClick={openPopup}
          />
          <Modal
            closePopup={() => {
              setIsPopupOpen(false);
            }}
            openPopup={isPopupOpen}
          >
            <section className="flex flex-col gap-8">
              <section className="flex flex-col items-center gap-5">
                {item?.bank && (
                  <figure className="relative h-[68px] w-[146px] rounded-lg border border-background200">
                    <ImageWithFallback
                      src={
                        getCdnPath(forwardProduct.bankLogo) ?? 'https://test-hkcdn.mncdn.com/images/bank/no-bank.svg'
                      }
                      alt={item.bank?.name}
                      title={item.bank?.name}
                      fill
                    />
                  </figure>
                )}
                {item?.name && (
                  <div className="flex flex-col gap-2">
                    <Heading tagType={'h3'} cls="text-lg text-neutral900 text-center leading-[28px]" bolded>
                      {`${forwardProduct?.bankName ?? 'Anlaşmalı bir HangiKredi KOBI Bankası'}'na yönlendiriliyorsunuz`}
                    </Heading>
                    <article
                      className="text-center text-sm leading-[28px] text-neutral500"
                      dangerouslySetInnerHTML={{
                        __html: forwardProduct?.redirectText ?? '',
                      }}
                    />
                  </div>
                )}
              </section>
              <section>
                {item && (
                  <LinkLikeButton
                    target={item.target}
                    href={`/basvuru/ticari-tasit-kredisi/${forwardProduct.redirectUrl}`}
                    text={item.redirectButtonText ?? 'Hemen Başvur'}
                    className="h-[48px] w-full rounded-lg text-sm after:ml-2 after:text-xl after:content-['↗'] md:text-base"
                  />
                )}
              </section>
            </section>
          </Modal>
        </>
      )}
    </div>
  );
};

type Props = {
  item: ResponseStoreFrontVehicleLoanDetail;
  forwardProduct: any;
  isMobile: boolean;
};

export const SummaryTable: React.FC<Props> = ({ item, forwardProduct, isMobile }) => {
  const interestOrShare = item.bank?.financeType === 2 ? 'Kâr Payı Oranı' : 'Faiz Oranı';
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const summaryContentObj = [
    {
      name: interestOrShare,
      value: `%${formatInterestRate(item.interestRate)}`,
      hasTooltip: true,
    },
    {
      name: 'Aylık taksit',
      value: fmt(item.interestRate, CurrencyTypeMissing.TRY),
    },
    {
      name: 'Toplam maliyet',
      value: fmt(item.interestRate, CurrencyTypeMissing.TRY),
    },
    {
      name: 'Yıllık maliyet oranı',
      value: formatInterestRate(item.interestRate),
    },
    {
      name: 'Banka komisyonu',
      value: item.interestRate ? 'Var' : 'Yok',
    },
  ];

  return !isMobile ? (
    <RoundedContainer className="flex-1 rounded-xl border border-neutral100 p-4 md:p-10">
      <Content
        summaryContentObj={summaryContentObj}
        item={item}
        forwardProduct={forwardProduct}
        openPopup={openPopup}
        setIsPopupOpen={setIsPopupOpen}
        isPopupOpen={isPopupOpen}
      />
    </RoundedContainer>
  ) : (
    <Content
      summaryContentObj={summaryContentObj}
      item={item}
      forwardProduct={forwardProduct}
      openPopup={openPopup}
      setIsPopupOpen={setIsPopupOpen}
      isPopupOpen={isPopupOpen}
    />
  );
};
